import React, { useEffect, useRef } from 'react';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import { ChatMessage } from '../../types';
import MessageBubble from './MessageBubble';
import ActionCard from './ActionCard';
import TypingIndicator from './TypingIndicator';
import InitialBubble from './InitialBubble';
import ConversationCost from './ConversationCost';
import styles from '../ChatWidget.module.scss';

function StreamingBubble(props: { text: string; isGrouped: boolean }) {
  const streamingMessage: ChatMessage = {
    role: 'assistant',
    content: props.text,
    timestamp: new Date(),
  };

  return <MessageBubble message={streamingMessage} isGrouped={props.isGrouped} />;
}

function isAssistantSide(role: string): boolean {
  return role === 'assistant' || role === 'tool-activity' || role === 'action-card';
}

export default function MessageList() {
  const {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
  } = useChatWidgetContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const showTypingIndicator = isStreaming && !streamingText && !isToolExecuting;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streamingText, showTypingIndicator]);
  const lastMessage = messages[messages.length - 1];
  const streamingIsGrouped = !!lastMessage && isAssistantSide(lastMessage.role);

  const lastToolActivityIndex = messages.reduce<number>(
    (acc, msg, i) => (msg.role === 'tool-activity' ? i : acc),
    -1,
  );

  const showWelcome = messages.length === 0 && !isStreaming;

  return (
    <div className={styles.messageList} ref={scrollRef} data-testid="chat-message-list">
      <div className={styles.dateSeparator}>Today</div>
      {showWelcome && <InitialBubble />}
      {messages.map((message, index) => {
        const prev = index > 0 ? messages[index - 1] : undefined;
        const grouped = !!prev
          && isAssistantSide(message.role)
          && isAssistantSide(prev.role);
        const isActiveToolCall = isToolExecuting
          && message.role === 'tool-activity'
          && index === lastToolActivityIndex;

        if (message.role === 'action-card' && message.pendingAction) {
          return (
            <ActionCard
              key={index}
              action={message.pendingAction}
              isGrouped={grouped}
            />
          );
        }

        return (
          <MessageBubble
            key={index}
            message={message}
            isGrouped={grouped}
            isActiveToolCall={isActiveToolCall}
          />
        );
      })}
      {showTypingIndicator && <TypingIndicator />}
      {streamingText && <StreamingBubble text={streamingText} isGrouped={streamingIsGrouped} />}
      <ConversationCost />
    </div>
  );
}
